import React, { ReactElement } from 'react';

import {
  AfterSubmitFunc,
  CancelButton,
  DateInput,
  FormTemplate,
  PriceInput,
  SubmitButton,
  TextArea,
  TextInput,
} from 'ui-builder';
import {
  ProjectDetailsFieldType,
  useProjectDetailsService,
} from 'nekst-api';
import { ValidationSchema } from 'validation-schema';
import { ProjectDetailFieldRow } from './types';

export type FieldData = Record<string, string | number | Date | null>;

interface EditFieldPopupProps {
  projectId: number;
  field: ProjectDetailFieldRow;
  afterSubmitFunc?: AfterSubmitFunc;
}

function buildValidationSchema(field: ProjectDetailFieldRow): ValidationSchema {
  const source = `${field.setting.id}`;

  if (field.setting.fieldType === ProjectDetailsFieldType.SHORT_TEXT) {
    return {
      [source]: {
        type: 'string',
        constraints: { maxLength: 255 },
      },
    };
  } else if (field.setting.fieldType === ProjectDetailsFieldType.LONG_TEXT) {
    return {
      [source]: {
        type: 'string',
        constraints: { maxLength: 1000 },
      },
    };
  } else {
    return {};
  }
}

function FieldInput(props: { field: ProjectDetailFieldRow }) {
  const source = `${props.field.setting.id}`;
  const label = props.field.setting.name;

  const inputMap: Record<ProjectDetailsFieldType, ReactElement> = {
    [ProjectDetailsFieldType.SHORT_TEXT]: (
      <TextInput source={source} label={label} />
    ),
    [ProjectDetailsFieldType.LONG_TEXT]: (
      <TextArea source={source} label={label} />
    ),
    [ProjectDetailsFieldType.PRICE]: (
      <PriceInput source={source} label={label} />
    ),
    [ProjectDetailsFieldType.DATE]: (
      <DateInput source={source} label={label} />
    ),
  };

  return inputMap[props.field.setting.fieldType];
}

export default function EditFieldPopup(props: EditFieldPopupProps) {
  const detailsService = useProjectDetailsService();
  const { field } = props;
  const source = `${field.setting.id}`;

  return (
    <FormTemplate<FieldData>
      title={`Edit ${field.setting.name}`}
      submitFormFunc={async (data) => {
        await detailsService.updateProjectDetails(props.projectId, data);
        return data;
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      initialData={{ [source]: field.value ?? null }}
      validationSchema={buildValidationSchema(field)}
      buttons={<><SubmitButton /><CancelButton /></>}
    >
      <FieldInput field={field} />
    </FormTemplate>
  );
}
